import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { Loader2, Upload, ShieldCheck, AlertTriangle, CreditCard, FileImage, Lock, CheckCircle2, MapPin, Clock } from "lucide-react";
import HintDot from "@/components/HintDot";

// The two order-step consents. Wording matches ORDER_CONSENT_LABELS in backend/server.py.
// Links open in a new tab so the customer never loses their one time link.
function OrderConsent({ consent, onChange }) {
  const row = "flex items-start gap-3 cursor-pointer";
  const box = "mt-0.5 w-4 h-4 rounded border-paw-border accent-paw-primary flex-shrink-0";
  const lnk = "text-paw-accent font-medium hover:underline";
  return (
    <div className="rounded-xl border border-paw-border bg-paw-surface-alt/40 px-4 py-4 space-y-3.5 mb-5" data-testid="order-consent-block">
      <label className={row} data-testid="order-consent-agreements-label">
        <input type="checkbox" className={box} checked={!!consent.order_agreements} data-testid="order-consent-agreements"
          onChange={(e) => onChange({ ...consent, order_agreements: e.target.checked })} />
        <span className="font-manrope text-xs text-paw-text-secondary leading-relaxed">
          <span className="block font-manrope font-medium text-[13px] text-paw-text">I agree to the Terms of Service, the Privacy Policy, and the Shipping, Refund, Cancellation and Returns Policy for this order.</span>
          <span className="block mt-1">
            Read the <a href="/terms" target="_blank" rel="noreferrer" className={lnk}>Terms of Service</a>, <a href="/privacy" target="_blank" rel="noreferrer" className={lnk}>Privacy Policy</a> and <a href="/shipping-refund" target="_blank" rel="noreferrer" className={lnk}>Shipping, Refund, Cancellation and Returns Policy</a>.
          </span>
        </span>
      </label>
      <label className={row} data-testid="order-consent-delivery-label">
        <input type="checkbox" className={box} checked={!!consent.delivery_timing} data-testid="order-consent-delivery"
          onChange={(e) => onChange({ ...consent, delivery_timing: e.target.checked })} />
        <span className="font-manrope text-xs text-paw-text-secondary leading-relaxed">
          <span className="block font-manrope font-medium text-[13px] text-paw-text">I understand delivery can take longer than the estimate shown.</span>
          <span className="block mt-1">
            Shipping times are estimates, not guarantees. Delays can happen because of prescription verification, pharmacy review, stock availability, payment checks, customs, courier handling, weather, or legal restrictions. These are outside PawLinx control. We track every order and keep you updated, and if we cannot fulfil your order you receive a full refund.
          </span>
        </span>
      </label>
    </div>
  );
}

export default function EstimateLinkPage() {
  const { token } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [paying, setPaying] = useState(false);
  const [petMeds, setPetMeds] = useState(null); // null | "yes" | "no"
  const [petMedsDetail, setPetMedsDetail] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [address, setAddress] = useState({ street: "", apt: "", city: "", state_province: "", postal_code: "", country: "" });
  const [savingAddress, setSavingAddress] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // Never pre-ticked. The backend rejects checkout unless both arrive explicitly true.
  const [orderConsent, setOrderConsent] = useState({ order_agreements: false, delivery_timing: false });
  const orderConsentDone = orderConsent.order_agreements && orderConsent.delivery_timing;

  const load = async () => {
    try {
      const { data } = await api.get(`/estimates/link/${token}`);
      if (data?.generating) {
        // Quote isn't ready yet — show the "preparing" state and keep the
        // previous estimate (if any) rather than rendering a $0 quote.
        setGenerating(true);
        return data;
      }
      setGenerating(false);
      setEstimate(data);
      if (data.scan_result) setScanResult(data.scan_result);
      // Never ask a returning customer the medication question twice. The backend persists the
      // answer when the prescription is uploaded, so rehydrate it whenever it comes back.
      const savedAnswer = String(data.pet_meds_answer || "").toLowerCase();
      if (savedAnswer === "yes" || savedAnswer === "no") setPetMeds(savedAnswer);
      if (data.pet_medication_info) setPetMedsDetail(data.pet_medication_info);
      if (data.shipping_address) setAddress((prev) => ({ ...prev, ...data.shipping_address }));
      return data;
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // While the backend is still preparing the quote, poll every ~3s until a
  // real quote arrives, then stop.
  useEffect(() => {
    if (!generating) return;
    const id = setInterval(() => { load(); }, 3000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [generating, token]);

  // Escape dismisses the post-upload review notice, the same as the Got it button.
  useEffect(() => {
    if (!showReviewModal) return;
    const onKey = (ev) => { if (ev.key === "Escape") setShowReviewModal(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showReviewModal]);

  // When the customer returns from Stripe Checkout (?paid=1&session_id=...), confirm the
  // payment directly with the backend rather than waiting on the Stripe webhook. This
  // creates the order and flips the estimate to "paid" even if the webhook never fires.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") !== "1") return;
    let cancelled = false;
    (async () => {
      setConfirming(true);
      const sessionId = params.get("session_id") || undefined;
      for (let i = 0; i < 8 && !cancelled; i++) {
        try {
          const { data } = await api.post(`/estimates/link/${token}/verify-payment`, { session_id: sessionId });
          if (data?.paid) { await load(); break; }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 1600));
      }
      if (!cancelled) setConfirming(false);
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [token]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    // The technician reviews the prescription against this answer, so the backend now requires it
    // to arrive with the file (server.py:3567-3584). Checked here too, so the customer is told
    // before a 10MB upload is spent.
    const answer = petMeds === "yes" || petMeds === "no" ? petMeds : "";
    if (!answer) { toast.error("Please answer the question about your pet's other medications and allergies first."); return; }
    const detail = answer === "yes" ? petMedsDetail.trim() : "";
    if (answer === "yes" && !detail) { toast.error("Please list your pet's other medications or allergies."); return; }
    setUploading(true); setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("pet_meds_answer", answer);
      fd.append("pet_medication_info", detail);
      const { data } = await api.post(`/estimates/link/${token}/upload-prescription`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanResult(data.scan);
      if (data.rx_review?.state === "pending") {
        // Queued for a human. Payment stays locked; say so plainly and prominently.
        setFile(null);
        setShowReviewModal(true);
        toast.success("Prescription received · with our pharmacy team");
      } else {
        toast.error(data.scan?.reason || "That file could not be read clearly. Please upload a clearer copy.");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      await api.post(`/estimates/link/${token}/address`, address);
      toast.success("Shipping address saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingAddress(false); }
  };

  const handleCheckout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/estimates/link/${token}/checkout`, {
        origin_url: window.location.origin,
        pet_medication_info: petMeds === "yes" ? petMedsDetail : null,
        consent: orderConsent,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPaying(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-paw-bg flex items-center justify-center px-6" data-testid="estimate-link-error">
      <div className="max-w-md w-full bg-white border border-paw-border rounded-2xl p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-paw-error/10 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-5 h-5 text-paw-error" />
        </div>
        <h1 className="font-outfit font-bold text-xl text-paw-text mb-2">Link Unavailable</h1>
        <p className="font-manrope text-sm text-paw-text-muted">{error}</p>
      </div>
    </div>
  );

  // Quote still being prepared by the backend — friendly waiting state that
  // auto-refreshes. Never show a $0 quote here.
  if (generating && !estimate) return (
    <div className="min-h-screen bg-paw-bg flex items-center justify-center px-6" data-testid="estimate-generating">
      <div className="max-w-md w-full bg-white border border-paw-border rounded-2xl p-10 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-paw-primary mx-auto mb-4" />
        <h1 className="font-outfit font-bold text-xl text-paw-text mb-2">Your quote is being prepared</h1>
        <p className="font-manrope text-sm text-paw-text-muted">This page will refresh in a moment. Please don't close this window.</p>
      </div>
    </div>
  );

  if (!estimate) return (
    <div className="min-h-screen bg-paw-bg flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-paw-text-muted" />
    </div>
  );

  const q = estimate.customer_quote || {
    medication: ((estimate.quote_breakdown?.base_cost || 0) + (estimate.quote_breakdown?.markup_amount || 0)).toFixed(2),
    shipping: estimate.quote_breakdown?.shipping || 0,
    total: estimate.quote_breakdown?.total || estimate.quote || 0,
  };
  const isPaid = estimate.status === "paid";
  const isInsurance = estimate.client_type === "insurance_referral" && estimate.insurance_partner_name;
  const savedAddr = estimate.shipping_address || {};
  const addressComplete = !!(savedAddr.street && savedAddr.city && savedAddr.postal_code && savedAddr.country);
  const needsAddress = !addressComplete;
  const medsStep = needsAddress ? 2 : 1;
  const rxStep = needsAddress ? 3 : 2;
  const payStep = needsAddress ? 4 : 3;

  // ---- Pharmacy-technician review gate -------------------------------------------------
  // Mirrors checkout_estimate in backend/server.py: payment is only reachable once the
  // technician has approved (or adjusted) the prescription. Two edge cases are folded in
  // deliberately: an estimate uploaded before this gate existed has no rx_review object but
  // a passing AI scan (treat as approved, never strand it), and status "rx_rejected" always
  // wins over a stale rx_review.state of "pending".
  const rr = estimate.rx_review || {};
  const hasRx = !!estimate.prescription_path;
  const scan = scanResult || estimate.scan_result || {};
  const aiReason = scan.reason || "";
  const rxState = estimate.status === "rx_rejected"
    ? "rejected"
    : rr.state || (hasRx ? (scan.passed ? "approved" : "rejected") : "");
  const payUnlocked = rxState === "approved" || rxState === "adjusted";
  const slaHours = estimate.review_sla_hours || 24;
  const requestedQty = rr.requested_quantity || "";
  const approvedQty = rr.approved_quantity || "";
  const ptNote = (rr.pt_note || "").trim();
  const firstName = estimate.full_name?.split(" ")[0] || "";
  const fmtWhen = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };
  const dueWhen = rxState === "pending" ? fmtWhen(rr.due_at) : "";
  const petMedsReady = petMeds === "no" || (petMeds === "yes" && petMedsDetail.trim().length > 0);
  const lockedHint = rxState === "pending"
    ? `Payment opens once our pharmacy technician approves your prescription, usually within ${slaHours} hours`
    : rxState === "rejected"
      ? "Please upload a clearer copy of your prescription to continue"
      : "Upload your veterinarian's prescription to continue";

  // The upload step is one-shot. It stays open only before the first file arrives, or after the
  // pharmacy technician rejects one and asks for a replacement. In every other state the file is
  // already with the technician, or already decided, so the form is swapped for a locked receipt.
  // Enforced again in the backend at upload_prescription_for_estimate, so this is not the only gate.
  const rxUploadOpen = rxState === "" || rxState === "rejected";
  const uploadedName = estimate.prescription_filename || "";
  const uploadLockedNote = rxState === "pending"
    ? "This step is complete and locked, so the file our pharmacy technician is reviewing cannot be replaced. If you sent the wrong document, reply to the email we just sent you and we will reopen this step for you."
    : "This step is complete and locked. Your reviewed prescription stays on file for this order, so there is nothing more to upload.";

  // Confirming the Stripe payment on return (webhook-independent) — show a clear
  // "processing" state instead of dropping the customer back on the quote page.
  if (confirming && !isPaid) return (
    <div className="min-h-screen bg-paw-bg flex items-center justify-center px-6" data-testid="estimate-confirming">
      <div className="max-w-md w-full bg-white border border-paw-border rounded-2xl p-10 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-paw-primary mx-auto mb-4" />
        <h1 className="font-outfit font-bold text-xl text-paw-text mb-2">Confirming your payment…</h1>
        <p className="font-manrope text-sm text-paw-text-muted">Hang tight, this only takes a moment. Please don't close this window.</p>
      </div>
    </div>
  );

  // Paid — dedicated confirmation view so the customer never lands back on the quote.
  if (isPaid) return (
    <div className="min-h-screen bg-paw-bg flex items-center justify-center px-6 py-12" data-testid="estimate-paid">
      <div className="max-w-md w-full bg-white border border-paw-border rounded-2xl p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-paw-success/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-paw-success" />
        </div>
        <h1 className="font-outfit font-bold text-2xl text-paw-text mb-2">{isInsurance ? "Order confirmed" : "Payment received"}</h1>
        <p className="font-manrope text-sm text-paw-text-muted mb-1">Thank you, {estimate.full_name?.split(" ")[0] || "there"}. Your order for <span className="font-semibold text-paw-text">{estimate.medication_name}</span> is now being processed.</p>
        <p className="font-manrope text-sm text-paw-text-muted mb-6">A licensed vet will review your prescription and we'll email you when it ships.</p>
        <a href="/dashboard" className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-xl font-manrope font-semibold text-sm transition-colors" data-testid="paid-dashboard-btn">
          Go to my dashboard
        </a>
        <p className="font-manrope text-xs text-paw-text-muted mt-5">Secured by Stripe · A receipt has been emailed to you.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paw-bg py-12 px-6" data-testid="estimate-link-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted mb-2">PawLinx · Personalized Quote</p>
          <h1 className="font-outfit font-bold text-3xl sm:text-4xl text-paw-text tracking-tight">Hi {estimate.full_name?.split(" ")[0]}, your quote is ready</h1>
          <p className="font-manrope text-sm text-paw-text-muted mt-3 max-w-xl">Upload your veterinarian's prescription below. A licensed pharmacy technician reviews it within {slaHours} hours, then payment opens right here on this same link.</p>
        </div>

        {/* Quote breakdown */}
        <div className="bg-white border border-paw-border rounded-2xl p-7 mb-6" data-testid="quote-breakdown">
          <p className="font-outfit font-semibold text-base text-paw-text mb-1">{estimate.medication_name}</p>
          <p className="font-manrope text-sm text-paw-text-muted mb-5">{estimate.strength} · {estimate.dosage_form} · {qtyDisplay(estimate)} · For {estimate.pet_type}{estimate.pet_name ? ` (${estimate.pet_name})` : ""}</p>
          {(q.dispensed_as || q.substitution_note) && (
            <div className="rounded-xl border border-paw-blue-100 bg-paw-blue-50 p-4 mb-5" data-testid="estimate-substitution">
              {q.dispensed_as && (
                <p className="font-manrope text-sm text-paw-text mb-1"><span className="font-semibold">Dispensed as:</span> {q.dispensed_as}</p>
              )}
              {q.substitution_note && (
                <p className="font-manrope text-[13px] text-paw-text-secondary leading-relaxed">{q.substitution_note}</p>
              )}
            </div>
          )}
          <div className="space-y-2.5 font-manrope text-sm border-t border-paw-border pt-5">
            <div className="flex justify-between text-paw-text-secondary"><span>Medication</span><span>${Number(q.medication).toFixed(2)}</span></div>
            <div className="flex justify-between text-paw-text-secondary"><span>Shipping</span><span>${Number(q.shipping).toFixed(2)}</span></div>
            <div className="flex justify-between pt-3 mt-3 border-t border-paw-border">
              <span className="font-outfit font-bold text-paw-text">Total</span>
              <span className="font-outfit font-bold text-2xl text-paw-accent">${Number(q.total).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Step 1 (conditional): Shipping address */}
        {needsAddress ? (
          <div className="bg-white border border-paw-border rounded-2xl p-7 mb-6" data-testid="address-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-paw-primary/10 flex items-center justify-center font-outfit font-bold text-sm text-paw-primary">1</div>
              <h2 className="font-outfit font-bold text-lg text-paw-text">Shipping address</h2>
            </div>
            <p className="font-manrope text-sm text-paw-text-muted mb-5">Where should we ship your medication? This is required before payment.</p>
            <form onSubmit={handleSaveAddress} className="space-y-3">
              <AddressAutocomplete
                value={address.street}
                onChange={(text) => setAddress((p) => ({ ...p, street: text }))}
                onSelect={(parts) => setAddress((p) => ({
                  ...p,
                  street: parts.street || parts.formatted || p.street,
                  city: parts.city || p.city,
                  state_province: parts.state_province || p.state_province,
                  postal_code: parts.postal_code || p.postal_code,
                  country: parts.country || p.country,
                }))}
                required
                placeholder="Start typing your address"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-paw-border bg-white text-sm font-manrope text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:border-paw-primary"
              />
              <input type="text" placeholder="Apt, suite, unit (optional)" value={address.apt} onChange={(e) => setAddress({ ...address, apt: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="addr-apt" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" required placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="addr-city" />
                <input type="text" placeholder="State / Province" value={address.state_province} onChange={(e) => setAddress({ ...address, state_province: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="addr-state" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" required placeholder="Postal / ZIP code" value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="addr-postal" />
                <input type="text" required placeholder="Country" value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="addr-country" />
              </div>
              <button type="submit" disabled={savingAddress} className="w-full h-11 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="addr-save-btn">
                {savingAddress ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><MapPin className="w-4 h-4" /> Save shipping address</>}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-white border border-paw-border rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="address-confirmed">
            <div className="w-8 h-8 rounded-full bg-paw-success/10 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-paw-success" /></div>
            <div>
              <p className="font-manrope text-sm font-semibold text-paw-text">Shipping to</p>
              <p className="font-manrope text-sm text-paw-text-muted">{savedAddr.street}{savedAddr.apt ? `, ${savedAddr.apt}` : ""}, {savedAddr.city}{savedAddr.state_province ? `, ${savedAddr.state_province}` : ""} {savedAddr.postal_code}, {savedAddr.country}</p>
            </div>
          </div>
        )}

        {/* Step: your pet's other medications. This deliberately comes BEFORE the upload now: the
            pharmacy technician reviews the prescription against this answer, and the upload
            endpoint refuses a submission that arrives without it (server.py:3567-3584). */}
        <div className="bg-white border border-paw-border rounded-2xl p-7 mb-6" data-testid="pet-meds-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-paw-primary/10 flex items-center justify-center font-outfit font-bold text-sm text-paw-primary">{medsStep}</div>
            <h2 className="font-outfit font-bold text-lg text-paw-text">Your pet's other medications</h2>
          </div>
          <p className="font-manrope text-sm text-paw-text-muted mb-4">Is your pet currently on any medication, or does it have any allergies to any medication?</p>
          <div className="flex gap-3 mb-4">
            <button
              type="button"
              onClick={() => setPetMeds("yes")}
              disabled={!rxUploadOpen}
              className={`flex-1 h-10 rounded-xl border-2 text-sm font-manrope font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${petMeds === "yes" ? "border-paw-primary bg-paw-primary/5 text-paw-primary" : "border-paw-border text-paw-text-muted hover:border-paw-primary/50"}`}
              data-testid="pet-meds-yes"
            >Yes</button>
            <button
              type="button"
              onClick={() => setPetMeds("no")}
              disabled={!rxUploadOpen}
              className={`flex-1 h-10 rounded-xl border-2 text-sm font-manrope font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${petMeds === "no" ? "border-paw-primary bg-paw-primary/5 text-paw-primary" : "border-paw-border text-paw-text-muted hover:border-paw-primary/50"}`}
              data-testid="pet-meds-no"
            >No</button>
          </div>
          {petMeds === "yes" && (
            <textarea
              rows={3}
              placeholder="Please describe the medications and/or allergies…"
              value={petMedsDetail}
              onChange={(e) => setPetMedsDetail(e.target.value)}
              disabled={!rxUploadOpen}
              className="w-full px-3 py-2.5 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary resize-none disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="pet-meds-detail"
            />
          )}
        </div>

        {/* Step: prescription upload, and where the pharmacy technician's review stands */}
        <div className="bg-white border border-paw-border rounded-2xl p-7 mb-6" data-testid="prescription-upload-section">
          <div className="relative flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-paw-primary/10 flex items-center justify-center font-outfit font-bold text-sm text-paw-primary">{rxStep}</div>
            <h2 className="font-outfit font-bold text-lg text-paw-text">Upload your prescription</h2>
            <HintDot hint="JPG, PNG or PDF, up to 10MB. Photograph it flat in good light and check that the medication, strength, quantity and refills box are all readable." testid="rx-upload" />
          </div>
          <p className="font-manrope text-sm text-paw-text-muted mb-5">A licensed pharmacy technician checks every prescription against the quantity you asked for before payment opens.</p>

          {/* Review status. Driven by the technician's decision (estimate.rx_review), never by the
              automated legibility check. Mirrors the backend gate at server.py:3752-3774. */}
          {rxState === "pending" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-paw-primary/8 border border-paw-primary/20 mb-4" data-testid="rx-status-pending">
              <Clock className="w-5 h-5 text-paw-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm font-semibold text-paw-text">Your prescription is under review</p>
                <p className="font-manrope text-xs text-paw-text-secondary mt-1 leading-relaxed">
                  Please allow up to {slaHours} hours. We'll email you at {estimate.email} as soon as our pharmacy technician has reviewed it. Payment then opens on this same link, and you will not need to upload anything again.{dueWhen ? ` Expected by ${dueWhen}.` : ""}
                </p>
              </div>
            </div>
          )}
          {rxState === "approved" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-paw-success/8 border border-paw-success/20 mb-4" data-testid="rx-status-approved">
              <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm font-semibold text-paw-text">Prescription approved{approvedQty ? ` for ${approvedQty}` : ""}</p>
                <p className="font-manrope text-xs text-paw-text-secondary mt-1 leading-relaxed">Our pharmacy technician has reviewed and approved your prescription. You can complete your payment below.{ptNote ? ` ${ptNote}` : ""}</p>
              </div>
            </div>
          )}
          {rxState === "adjusted" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 mb-4" data-testid="rx-status-adjusted">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm font-semibold text-paw-text">Quantity adjusted after review</p>
                <p className="font-manrope text-xs text-paw-text-secondary mt-1 leading-relaxed">
                  Your prescription authorises {approvedQty || "a smaller quantity"}{requestedQty ? ` rather than the ${requestedQty} requested` : ""}, so we have updated your quote. The total shown above is what you will pay. There is nothing to re-upload; you can complete payment below.{ptNote ? ` ${ptNote}` : ""}
                </p>
              </div>
            </div>
          )}
          {rxState === "rejected" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-paw-error/8 border border-paw-error/20 mb-4" data-testid="rx-status-rejected">
              <AlertTriangle className="w-5 h-5 text-paw-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm font-semibold text-paw-text">We could not accept that file</p>
                <p className="font-manrope text-xs text-paw-text-secondary mt-1 leading-relaxed">{ptNote || aiReason || "The prescription we received could not be read clearly."} Please upload a clearer copy below. A photo taken flat in good light usually does it.</p>
              </div>
            </div>
          )}

          {rxUploadOpen ? (
            <form onSubmit={handleUpload} className="space-y-4">
              <label className={`block border-2 border-dashed ${file ? "border-paw-primary/40 bg-paw-primary/5" : "border-paw-border bg-paw-bg"} rounded-xl px-6 py-8 text-center cursor-pointer hover:border-paw-primary/60 transition-colors`}>
                <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="prescription-file-input" />
                <FileImage className="w-6 h-6 text-paw-text-muted mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-manrope text-sm text-paw-text">{file ? file.name : "Click to choose file"}</p>
              </label>

              <button type="submit" disabled={!file || uploading || !petMedsReady} className="w-full h-11 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="prescription-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload Prescription</>}
              </button>
              {!petMedsReady && (
                <p className="font-manrope text-xs text-paw-text-secondary text-center flex items-center gap-1.5 justify-center" data-testid="rx-upload-blocked-note">
                  <Lock className="w-3 h-3" /> {petMeds === "yes" ? "Please describe the medications or allergies above" : "Please answer the medication question above first"}
                </p>
              )}
            </form>
          ) : (
            <div className="flex items-start gap-3 px-4 py-4 rounded-xl bg-paw-bg border border-paw-border" data-testid="rx-upload-locked">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="font-manrope text-sm font-semibold text-paw-text">Prescription received</p>
                {uploadedName && (
                  <p className="font-manrope text-xs text-paw-text-secondary mt-1 break-all" data-testid="rx-upload-locked-file">{uploadedName}</p>
                )}
                <p className="font-manrope text-xs text-paw-text-secondary mt-1.5 leading-relaxed">{uploadLockedNote}</p>
              </div>
              <Lock className="w-4 h-4 text-paw-text-secondary flex-shrink-0 mt-0.5" data-testid="rx-upload-locked-icon" />
            </div>
          )}
        </div>

        {/* Step 2: Payment */}
        <div className="bg-white border border-paw-border rounded-2xl p-7" data-testid="payment-section">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-outfit font-bold text-sm ${payUnlocked ? "bg-paw-primary/10 text-paw-primary" : "bg-paw-border text-paw-text-muted"}`}>{payStep}</div>
            <h2 className="font-outfit font-bold text-lg text-paw-text">{isInsurance ? "Confirm your order" : "Complete payment"}</h2>
          </div>

          {needsAddress && !isPaid && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-paw-warning/10 border border-paw-warning/30 mb-5" data-testid="address-required-note">
              <MapPin className="w-5 h-5 text-paw-warning flex-shrink-0 mt-0.5" />
              <p className="font-manrope text-sm text-paw-text">Please add your shipping address above before payment.</p>
            </div>
          )}

          {isPaid ? (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-paw-success/8 border border-paw-success/20">
              <CheckCircle2 className="w-5 h-5 text-paw-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm font-semibold text-paw-text">{isInsurance ? "Order confirmed" : "Payment received"}</p>
                <p className="font-manrope text-xs text-paw-text-muted mt-0.5">Your order is being processed. We'll email you when it ships.</p>
              </div>
            </div>
          ) : isInsurance ? (
            <>
              <div className="bg-paw-primary/5 border border-paw-primary/15 rounded-xl px-4 py-3 mb-5">
                <p className="font-manrope text-sm text-paw-text leading-relaxed"><span className="font-semibold">Covered by {estimate.insurance_partner_name}.</span> There's nothing for you to pay. Confirm to send your order for veterinary review and fulfillment.</p>
              </div>
              <OrderConsent consent={orderConsent} onChange={setOrderConsent} />
              <button onClick={handleCheckout} disabled={!payUnlocked || paying || needsAddress || !orderConsentDone} className="w-full h-11 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="checkout-btn">
                {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><CheckCircle2 className="w-4 h-4" /> Confirm &amp; submit order</>}
              </button>
              {!payUnlocked && <p className="font-manrope text-xs text-paw-text-secondary text-center mt-3 flex items-center gap-1.5 justify-center" data-testid="pay-locked-note"><Lock className="w-3 h-3" /> {lockedHint}</p>}
            </>
          ) : (
            <>
              <p className="font-manrope text-sm text-paw-text-muted mb-5">You'll be charged <span className="font-semibold text-paw-text">${q.total}</span> via Stripe. Payment only opens once our pharmacy technician has approved your prescription, so you are never charged for a quantity we cannot dispense.</p>
              <div className="bg-paw-primary/5 border border-paw-primary/15 rounded-xl px-4 py-3 mb-5">
                <p className="font-manrope text-[13px] text-paw-text-secondary leading-relaxed"><span className="font-semibold text-paw-text">Refund guarantee.</span> In the unlikely event that we can't fulfill your order, you'll receive a <strong>full refund</strong> automatically. No questions asked.</p>
              </div>
              <OrderConsent consent={orderConsent} onChange={setOrderConsent} />
              <button onClick={handleCheckout} disabled={!payUnlocked || paying || needsAddress || !orderConsentDone} className="w-full h-11 bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="checkout-btn">
                {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><CreditCard className="w-4 h-4" /> Pay ${q.total} Securely</>}
              </button>
              {!payUnlocked && <p className="font-manrope text-xs text-paw-text-secondary text-center mt-3 flex items-center gap-1.5 justify-center" data-testid="pay-locked-note"><Lock className="w-3 h-3" /> {lockedHint}</p>}
            </>
          )}
        </div>

        <p className="font-manrope text-xs text-paw-text-muted text-center mt-8">PawLinx · Pet Medication Referral Service · Keep this link: it stays active through prescription review and payment</p>
      </div>

      {/* Post-upload notice. The prescription is queued for a pharmacy technician and payment
          stays locked until they act on it. Wording mirrors the confirmation email the backend
          sends (_send_rx_received_email), so the two never contradict each other. */}
      {showReviewModal && (
        <div className="fixed inset-0 z-[200] bg-paw-navy/60 flex items-center justify-center px-6 py-10" data-testid="rx-review-modal" role="dialog" aria-modal="true" aria-labelledby="rx-review-modal-title">
          <div className="bg-white rounded-2xl w-full max-w-lg p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-paw-primary/10 grid place-items-center mb-5">
              <Clock className="w-6 h-6 text-paw-primary" />
            </div>
            <h2 id="rx-review-modal-title" className="font-outfit font-bold text-xl text-paw-text mb-3">Your prescription is with our pharmacy team</h2>
            <p className="font-manrope text-sm text-paw-text-secondary leading-relaxed mb-3">
              Thank you{firstName ? `, ${firstName}` : ""}. We have received your prescription. A licensed pharmacy technician now reviews it. Please allow us up to {slaHours} hours to complete that review.
            </p>
            <p className="font-manrope text-sm text-paw-text-secondary leading-relaxed mb-6">
              We will email you at <span className="font-semibold text-paw-text">{estimate.email}</span> as soon as the review is finished. Payment then opens on this same link. There is nothing further to upload.
            </p>
            <button type="button" onClick={() => setShowReviewModal(false)} className="w-full h-11 bg-paw-accent hover:bg-paw-accent-hover text-white rounded-xl font-manrope font-semibold text-sm transition-colors" data-testid="rx-review-modal-close">
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
